import { TrendingDownIcon, TrendingUpIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { cn } from '@/lib/utils';
import {
  MetricTooltipCard,
  MetricTooltipRow,
} from '@/modules/analytics/components/tooltip/MetricTooltipCard';
import { selectAnalyticsDates } from '@/modules/analytics/redux/selectors';

import { computePercentChange } from './PercentChangeIndicator';
import { formatDateRangeLabel } from '../../utils/formatDateRangeLabel';

const INVERTED_COLORS = { up: 'text-destructive', down: 'text-successful' } as const;

interface Props {
  current: number | null | undefined;
  previous: number | null | undefined;
  formatValue: (value: number) => string;
  /** Pre-translated tooltip label for the current period (defaults to "This period"). */
  thisPeriodLabel?: string;
  /** Pre-translated tooltip label for the prior period (defaults to "Previous period"). */
  previousPeriodLabel?: string;
  /** Set when a decrease is favorable (e.g. hallucinations, response time). */
  invertColors?: boolean;
  className?: string;
}

const formatDateHeader = (startDate: string, endDate: string) =>
  formatDateRangeLabel(startDate, endDate, 'MMM d, yyyy');

export const CompareTrendCell = ({
  current,
  previous,
  formatValue,
  thisPeriodLabel,
  previousPeriodLabel,
  invertColors,
  className,
}: Props) => {
  const { t } = useTranslation();
  const { startDate, endDate } = useSelector(selectAnalyticsDates);

  const { color, direction } = computePercentChange(current, previous);
  if (direction === 'neutral') {
    return null;
  }

  const effectiveColor = invertColors ? INVERTED_COLORS[direction] : color;
  const showDownArrow = invertColors ? direction === 'up' : direction === 'down';
  const TrendIcon = showDownArrow ? TrendingDownIcon : TrendingUpIcon;

  const resolvedThisPeriodLabel = thisPeriodLabel ?? t('analytics.overview_new.this_period');
  const resolvedPreviousPeriodLabel =
    previousPeriodLabel ?? t('analytics.overview_new.previous_period');

  const tooltipContent = (
    <MetricTooltipCard title={formatDateHeader(startDate, endDate)} className="min-w-44">
      <MetricTooltipRow
        label={resolvedThisPeriodLabel}
        value={current != null ? formatValue(current) : '–'}
      />
      <MetricTooltipRow
        label={resolvedPreviousPeriodLabel}
        value={previous != null ? formatValue(previous) : '–'}
      />
    </MetricTooltipCard>
  );

  return (
    <InfoTooltip
      content={tooltipContent}
      variant="rich"
      size="auto"
      showArrow={false}
      className="border-none bg-transparent p-0 shadow-none backdrop-blur-none"
    >
      <TrendIcon
        className={cn('size-4 shrink-0', effectiveColor, className)}
        aria-hidden="false"
        aria-label={`${resolvedThisPeriodLabel}: ${current != null ? formatValue(current) : '–'}, ${resolvedPreviousPeriodLabel}: ${previous != null ? formatValue(previous) : '–'}`}
      />
    </InfoTooltip>
  );
};
